import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCheckbox } from '../primitives/CDCheckbox';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface LoanDisclosuresData {
  assumption: {
    allowed: boolean;
  };
  demandFeature: {
    hasDemandFeature: boolean;
  };
  latePayment: {
    daysAfterDue: number;
    feeAmount: number;
    feeIsPercentage: boolean;
  };
  negativeAmortization: {
    hasNegativeAmortization: boolean;
  };
  partialPayments: {
    mayAccept: boolean;
    mayHoldInAccount: boolean;
    mayApplyToLoan: boolean;
    mayReturnToBorrower: boolean;
  };
  securityInterest: {
    propertyAddress: string;
  };
  escrowAccount: {
    hasEscrow: boolean;
    willHaveEscrow?: string;
    willNotHaveEscrow?: string;
  };
}

export interface CDLoanDisclosuresProps {
  /** Loan disclosures data */
  data: Partial<LoanDisclosuresData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<LoanDisclosuresData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDLoanDisclosures - Loan Disclosures section on Page 4
 *
 * Contains various disclosure items required by CFPB.
 */
export const CDLoanDisclosures = React.forwardRef<HTMLDivElement, CDLoanDisclosuresProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    // Helper to update nested fields
    const updateAssumption = (allowed: boolean) => {
      onChange?.({ ...data, assumption: { ...data.assumption, allowed } });
    };

    const updateDemandFeature = (hasDemandFeature: boolean) => {
      onChange?.({ ...data, demandFeature: { ...data.demandFeature, hasDemandFeature } });
    };

    const updateLatePayment = (updates: Partial<LoanDisclosuresData['latePayment']>) => {
      onChange?.({
        ...data,
        latePayment: { ...data.latePayment, ...updates } as LoanDisclosuresData['latePayment'],
      });
    };

    const updateNegativeAmortization = (hasNegativeAmortization: boolean) => {
      onChange?.({
        ...data,
        negativeAmortization: { ...data.negativeAmortization, hasNegativeAmortization },
      });
    };

    const updatePartialPayments = (
      field: keyof LoanDisclosuresData['partialPayments'],
      value: boolean,
    ) => {
      const currentPartialPayments = data.partialPayments ?? {
        mayAccept: false,
        mayHoldInAccount: false,
        mayApplyToLoan: false,
        mayReturnToBorrower: false,
      };
      onChange?.({ ...data, partialPayments: { ...currentPartialPayments, [field]: value } });
    };

    const updateSecurityInterest = (propertyAddress: string) => {
      onChange?.({ ...data, securityInterest: { ...data.securityInterest, propertyAddress } });
    };

    const updateEscrowAccount = (hasEscrow: boolean) => {
      onChange?.({ ...data, escrowAccount: { ...data.escrowAccount, hasEscrow } });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Loan Disclosures" />

        <div className="border border-t-0 border-cd-black">
          <div className="grid grid-cols-2 gap-0">
            {/* Left Column */}
            <div className="border-r border-cd-black">
              {/* Assumption */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Assumption</div>
                <div className="cd-text-tiny">
                  If you sell or transfer this property to another person, your lender
                </div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.assumption?.allowed === true}
                    size="sm"
                    onChange={editable ? () => updateAssumption(true) : undefined}
                  />
                  <span>
                    will allow, under certain conditions, this person to assume this loan on the
                    original terms.
                  </span>
                </div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.assumption?.allowed === false}
                    size="sm"
                    onChange={editable ? () => updateAssumption(false) : undefined}
                  />
                  <span>will not allow assumption of this loan on the original terms.</span>
                </div>
              </div>

              {/* Demand Feature */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Demand Feature</div>
                <div className="cd-text-tiny">Your loan</div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.demandFeature?.hasDemandFeature === true}
                    size="sm"
                    onChange={editable ? () => updateDemandFeature(true) : undefined}
                  />
                  <span>has a demand feature</span>
                </div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.demandFeature?.hasDemandFeature === false}
                    size="sm"
                    onChange={editable ? () => updateDemandFeature(false) : undefined}
                  />
                  <span>does not have a demand feature</span>
                </div>
              </div>

              {/* Late Payment */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Late Payment</div>
                <div className="cd-text-tiny">
                  If your payment is more than{' '}
                  <span className="font-bold underline">
                    <CDEditableText
                      value={data.latePayment?.daysAfterDue?.toString() || '___'}
                      editable={editable}
                      onChange={(v) =>
                        updateLatePayment({ daysAfterDue: Number.parseInt(v, 10) || 0 })
                      }
                      size="tiny"
                      className="inline min-w-[2rem]"
                    />
                  </span>{' '}
                  days late, you will be charged a fee of{' '}
                  <span className="font-bold underline">
                    <CDEditableText
                      value={
                        data.latePayment?.feeIsPercentage
                          ? `${data.latePayment?.feeAmount}%`
                          : `$${data.latePayment?.feeAmount?.toFixed(2) || '___'}`
                      }
                      editable={editable}
                      onChange={(v) => {
                        const isPercentage = v.includes('%');
                        const numValue = Number.parseFloat(v.replace(/[$%]/g, '')) || 0;
                        updateLatePayment({ feeAmount: numValue, feeIsPercentage: isPercentage });
                      }}
                      size="tiny"
                      className="inline min-w-[3rem]"
                    />
                  </span>
                  {data.latePayment?.feeIsPercentage &&
                    ' of your monthly principal and interest payment'}
                  .
                </div>
              </div>

              {/* Negative Amortization */}
              <div className="p-1.5">
                <div className="cd-text-small font-bold mb-0.5">
                  Negative Amortization (Increase in Loan Amount)
                </div>
                <div className="cd-text-tiny">Under your loan terms, you</div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.negativeAmortization?.hasNegativeAmortization === true}
                    size="sm"
                    onChange={editable ? () => updateNegativeAmortization(true) : undefined}
                  />
                  <span>
                    are scheduled to make monthly payments that do not pay all of the interest due
                    that month. As a result, your loan amount will increase (negatively amortize).
                  </span>
                </div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.negativeAmortization?.hasNegativeAmortization === false}
                    size="sm"
                    onChange={editable ? () => updateNegativeAmortization(false) : undefined}
                  />
                  <span>do not have a negative amortization feature.</span>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              {/* Partial Payments */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Partial Payments</div>
                <div className="cd-text-tiny mb-0.5">Your lender</div>
                <div className="flex items-start gap-1 mb-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.partialPayments?.mayAccept}
                    size="sm"
                    onChange={
                      editable
                        ? (checked) => updatePartialPayments('mayAccept', checked)
                        : undefined
                    }
                  />
                  <span>
                    may accept payments that are less than the full amount due (partial payments)
                    and apply them to your loan.
                  </span>
                </div>
                <div className="flex items-start gap-1 mb-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.partialPayments?.mayHoldInAccount}
                    size="sm"
                    onChange={
                      editable
                        ? (checked) => updatePartialPayments('mayHoldInAccount', checked)
                        : undefined
                    }
                  />
                  <span>
                    may hold them in a separate account until you pay the rest of the payment.
                  </span>
                </div>
                <div className="flex items-start gap-1 cd-text-tiny">
                  <CDCheckbox
                    checked={data.partialPayments?.mayReturnToBorrower}
                    size="sm"
                    onChange={
                      editable
                        ? (checked) => updatePartialPayments('mayReturnToBorrower', checked)
                        : undefined
                    }
                  />
                  <span>does not accept any partial payments.</span>
                </div>
              </div>

              {/* Security Interest */}
              <div className="border-b border-cd-black p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Security Interest</div>
                <div className="cd-text-tiny">You are granting a security interest in</div>
                <div className="cd-text-tiny font-bold mt-0.5 underline">
                  <CDEditableText
                    value={data.securityInterest?.propertyAddress || '_______________'}
                    editable={editable}
                    onChange={(v) => updateSecurityInterest(v)}
                    size="tiny"
                    className="font-bold"
                  />
                </div>
                <div className="cd-text-tiny mt-0.5">
                  You may lose this property if you do not make your payments or satisfy other
                  obligations for this loan.
                </div>
              </div>

              {/* Escrow Account */}
              <div className="p-1.5">
                <div className="cd-text-small font-bold mb-0.5">Escrow Account</div>
                <div className="cd-text-tiny">For now, your loan</div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.escrowAccount?.hasEscrow === true}
                    size="sm"
                    onChange={editable ? () => updateEscrowAccount(true) : undefined}
                  />
                  <span>
                    will have an escrow account (also called an "impound" or "trust" account) for
                    paying the property costs listed below. Without an escrow account, you would pay
                    them directly, possibly in one or two large payments a year. Your lender may be
                    liable for penalties and interest for failing to make a payment.
                  </span>
                </div>
                <div className="flex items-start gap-1 mt-0.5 cd-text-tiny">
                  <CDCheckbox
                    checked={data.escrowAccount?.hasEscrow === false}
                    size="sm"
                    onChange={editable ? () => updateEscrowAccount(false) : undefined}
                  />
                  <span>
                    will not have an escrow account because you declined it. You must directly pay
                    your property costs, such as taxes and homeowner's insurance.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDLoanDisclosures.displayName = 'CDLoanDisclosures';
